import React, { useState, useEffect } from 'react';
import { X, Trash2, Eye, GitCompare, Calendar, MapPin, Building2, DollarSign, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { formatCurrency } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SavedScenarios = ({ 
  isOpen, 
  onClose, 
  officerId, 
  onLoadScenario,
  onCompareScenarios 
}) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (isOpen && officerId) {
      fetchScenarios();
    }
  }, [isOpen, officerId]);

  const fetchScenarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/scenarios/${officerId}`);
      if (!response.ok) throw new Error('Failed to fetch scenarios');
      const data = await response.json();
      setScenarios(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (scenarioId) => {
    if (!window.confirm('Are you sure you want to delete this scenario?')) return;
    
    setDeleting(scenarioId);
    try {
      const response = await fetch(`${API_URL}/api/scenarios/${officerId}/${scenarioId}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete scenario');
      setScenarios(scenarios.filter(s => s.id !== scenarioId));
      setSelectedForCompare(selectedForCompare.filter(id => id !== scenarioId));
    } catch (err) {
      alert('Failed to delete scenario');
    } finally {
      setDeleting(null);
    }
  };

  const handleLoad = async (scenarioId) => {
    try {
      const response = await fetch(`${API_URL}/api/scenarios/${officerId}/${scenarioId}`);
      if (!response.ok) throw new Error('Failed to load scenario');
      const data = await response.json();
      onLoadScenario(data);
      onClose();
    } catch (err) {
      alert('Failed to load scenario');
    }
  };

  const toggleCompareSelection = (scenarioId) => {
    if (selectedForCompare.includes(scenarioId)) {
      setSelectedForCompare(selectedForCompare.filter(id => id !== scenarioId));
    } else if (selectedForCompare.length < 3) {
      setSelectedForCompare([...selectedForCompare, scenarioId]);
    }
  };

  const handleCompare = async () => {
    if (selectedForCompare.length < 2) {
      alert('Please select at least 2 scenarios to compare');
      return;
    }
    
    try {
      const scenarioDetails = await Promise.all(
        selectedForCompare.map(async (id) => {
          const response = await fetch(`${API_URL}/api/scenarios/${officerId}/${id}`);
          if (!response.ok) throw new Error('Failed to load scenario');
          return response.json();
        })
      );
      onCompareScenarios(scenarioDetails);
      onClose();
    } catch (err) {
      alert('Failed to load scenarios for comparison');
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-[#002F87]">Saved Scenarios</h2>
            <p className="text-sm text-slate-500">Load or compare your saved loan scenarios</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#002F87]" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
              <Button onClick={fetchScenarios} variant="outline" className="mt-4">
                Try Again
              </Button>
            </div>
          ) : scenarios.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No saved scenarios yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Save your first scenario using the "Save Scenario" button
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {scenarios.map((scenario) => (
                <div 
                  key={scenario.id}
                  className={`border rounded-lg p-4 transition-all ${
                    selectedForCompare.includes(scenario.id) 
                      ? 'border-[#002F87] bg-blue-50/50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox for compare */}
                    <div className="pt-1">
                      <Checkbox
                        checked={selectedForCompare.includes(scenario.id)}
                        onCheckedChange={() => toggleCompareSelection(scenario.id)}
                        disabled={!selectedForCompare.includes(scenario.id) && selectedForCompare.length >= 3}
                        data-testid={`checkbox-scenario-${scenario.id}`}
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <p className="font-medium text-slate-900 truncate" data-testid={`scenario-address-${scenario.id}`}>
                          {scenario.property_address || 'No address'}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-xs font-medium">
                          {scenario.loan_type_label}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(scenario.created_at)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {formatCurrency(scenario.loan_amount)}
                        </span>
                      </div>
                      
                      {scenario.project_name && (
                        <p className="text-xs text-slate-400 mt-1 truncate">
                          Project: {scenario.project_name}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLoad(scenario.id)}
                        className="text-[#002F87] hover:text-[#002F87] hover:bg-blue-50"
                        data-testid={`btn-load-${scenario.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Load
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(scenario.id)}
                        disabled={deleting === scenario.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`btn-delete-${scenario.id}`}
                      >
                        {deleting === scenario.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {scenarios.length > 0 && (
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {selectedForCompare.length > 0 
                  ? `${selectedForCompare.length} scenario${selectedForCompare.length > 1 ? 's' : ''} selected`
                  : 'Select scenarios to compare (max 3)'}
              </p>
              <Button
                onClick={handleCompare}
                disabled={selectedForCompare.length < 2}
                className="bg-[#002F87] hover:bg-[#001a4d]"
                data-testid="btn-compare-scenarios"
              >
                <GitCompare className="w-4 h-4 mr-2" />
                Compare Selected
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedScenarios;
